import { Injectable, NgZone, inject, signal } from '@angular/core';

export type FlashType = 'success' | 'danger' | 'warning' | 'info';

export interface FlashItem {
  id: number;
  type: FlashType;
  text: string;
  showClose: boolean;
}

/**
 * 旧版 angular-flash v2.5（Flash.create(type, text, timeout, showClose)）相当。
 * 通知は $rootScope.flashes と同じくアプリ全体で共有し、画面遷移後も timeout まで残る。
 * 表示位置は各画面の <app-flash-message>（旧版 <flash-message> の位置）で決める。
 */
@Injectable({ providedIn: 'root' })
export class FlashService {
  private zone = inject(NgZone);
  private seq = 0;
  private timers = new Map<number, ReturnType<typeof setTimeout>>();
  readonly flashes = signal<FlashItem[]>([]);

  create(type: FlashType, text: string, timeout = 5000, showClose = true): number {
    const id = ++this.seq;
    this.flashes.update(list => [...list, { id, type, text, showClose }]);
    if (timeout > 0) {
      // zone外でタイマーを張り、表示中もアプリを安定状態に保つ
      this.timers.set(id, this.zone.runOutsideAngular(() =>
        setTimeout(() => this.zone.run(() => this.dismiss(id)), timeout)));
    }
    return id;
  }

  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) clearTimeout(timer);
    this.timers.delete(id);
    this.flashes.update(list => list.filter(item => item.id !== id));
  }

  clear(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    this.flashes.set([]);
  }
}
